import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { typeMetaClass, typeProseClass, typeCaptionClass } from "@/lib/typography";
import { HighlightedExample } from "../HighlightedExample";

/** Shared compact section label used across all practice modes. */
export const SectionLabel = ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <h3 className={cn(typeMetaClass, "mb-1.5 flex items-center gap-1.5", className)}>
        <span aria-hidden="true" className="inline-block h-1 w-1 rounded-full bg-primary/60" />
        {children}
    </h3>
);

/** Standard mode eyebrow — the small instruction above a prompt. */
export const PromptEyebrow = ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <p className={cn(typeMetaClass, "mb-3 text-center", className)}>{children}</p>
);

/**
 * Canonical "Definition" block — a labeled definition rendered identically in
 * every mode's prompt and in the post-answer reveal. The label is chrome; the
 * definition itself is the learning content and is rendered verbatim.
 */
export const DefinitionBlock = ({ definition, className }: { definition: string; className?: string }) => {
    const { t } = useTranslation();
    return (
        <div className={className}>
            <SectionLabel>{t("pages.vocabularyPractice.sections.definition", "Definition")}</SectionLabel>
            <p className={cn(typeProseClass, "break-words [overflow-wrap:anywhere]")}>{definition}</p>
        </div>
    );
};

/**
 * Canonical "Example" block — a labeled, italic example sentence with a light
 * left accent, rendered identically in every mode. Renders nothing when there
 * is no example.
 *
 * Deck words inside the sentence are highlighted, so a practice card shows
 * the learner's own vocabulary in context.
 */
export const ExampleBlock = ({ example, word, className }: { example?: string | null; word?: string | null; className?: string }) => {
    const { t } = useTranslation();
    if (!example) return null;
    return (
        <div className={className}>
            <SectionLabel>{t("pages.vocabularyPractice.sections.example", "Example")}</SectionLabel>
            <HighlightedExample
                text={example}
                word={word}
                className={cn(typeCaptionClass, "border-l-2 border-primary/30 pl-3 italic break-words [overflow-wrap:anywhere] dark:text-stone-300")}
            />
        </div>
    );
};
